import type { DependencyList } from 'react';
import { useEffect } from 'react';
import { isFunction } from '../typeUtils';

function isAsyncGenerator(
  val: AsyncGenerator<void, void, void> | Promise<void>
): val is AsyncGenerator<void, void, void> {
  // @ts-ignore
  return isFunction(val[Symbol.asyncIterator]);
}

/**
 * Hooks into `useEffect` to run an async function on mount, handles cancellation during cleanup, and prevents state updates after the component unmounts.
 *
 * The hook ignores errors from the async function — there is no `catch` block or `onError` callback. The `isAsyncGenerator` helper used inside is file-local and not exported.
 *
 * @param effect - An effect factory that returns either a `Promise<void>` or an `AsyncGenerator<void, void, void>`. Executed synchronously on mount; the resulting promise/async generator drives the effect logic.
 * @param deps - The dependency list passed to the underlying `useEffect` call.
 */
export function useAsyncEffect(
  effect: () => AsyncGenerator<void, void, void> | Promise<void>,
  deps?: DependencyList
) {
  useEffect(() => {
    const e = effect();
    let cancelled = false;

    async function execute() {
      if (isAsyncGenerator(e)) {
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const result = await e.next();
          if (result.done || cancelled) {
            break;
          }
        }
      } else {
        await e;
      }
    }

    execute();
    return () => {
      cancelled = true;
    };
  }, deps);
}

export default useAsyncEffect;
